import { Component, Method, Element, Host, State, Prop, Event, EventEmitter, Watch, h } from '@stencil/core';
import { PnColors, PnLanguages, PnUploadFileItem } from '@/globals/types';
import { getBytesFromHumanReadableFileSize, uuidv4, en, ripple, awaitTopbar } from '@/globals/helpers';
import { PnFileUploadError } from './pn-file-upload-error';
import { trash_can, upload, alert_exclamation_circle, attachment, check } from 'pn-design-assets/pn-assets/icons.js';
import { translations } from './translation';

/**
 * Users can drop files directly into the upload area of the component
 * or simply click on it to open their native file explorer.
 *
 * @nativeChange Use the `change` event to listen when the input receives files.
 *
 * @nativeCancel The `cancel` event lets you know the user canceled their selection.
 * Either via clicking on the "Cancel" button or pressing `ESC`.
 * */
@Component({
  tag: 'pn-file-upload',
  styleUrl: 'pn-file-upload.scss',
})
export class PnFileUpload {
  id: string = `pn-file-upload-${uuidv4()}`;
  idFileUpload: string = `${this.id}-label`;
  idHelpertext: string = `${this.id}-helpertext`;
  idAccepttext: string = `${this.id}-accept`;

  fileInputElement: HTMLInputElement;
  invalid: boolean = false;
  maxSizeInBytes: number = 0;

  @Element() hostElement: HTMLElement;

  @State() files: PnUploadFileItem[] = [];
  @State() isHovering: boolean = false;

  /** The default label is "Click or drag a file here". */
  @Prop() label?: string;
  /**
   * The helpertext defaults to "Supported formats {x} and max filesize {y}",
   * if there is a value set for the prop `accept`.
   **/
  @Prop() helpertext?: string;

  /** Manually set the language. @category Features */
  @Prop() language?: PnLanguages = null;
  /** Always hide the progress bar, even when upload has begun. @category Features  */
  @Prop() hideProgress: boolean = false;

  /** Set a HTML name. @category HTML attributes */
  @Prop() name: string;
  /** Set which file types the input accepts. Ex: .docs,.xml @category HTML attributes */
  @Prop() accept: string;
  /** Set the maximum upload size. @category HTML attributes */
  @Prop() maxSize: string = '';
  /** Use the capture mode. @category HTML attributes */
  @Prop() capture?: '' | 'user' | 'environment';
  /**
   * Set maximum amount of files to be selected.
   * Any value above 1 will force the `multiple` to be `true`.
   * @category HTML attributes
   **/
  @Prop() limit: number = 1;
  /** Allow multiple files to be selected. Will be overwritten to true, if `limit` is above 1. @category HTML attributes */
  @Prop({ mutable: true }) multiple: boolean;
  /** Set the input as required. @category HTML attributes */
  @Prop() required?: boolean = false;
  /** Disable the input. @category HTML attributes */
  @Prop() disabled: boolean = false;

  @Watch('limit')
  handleLimit() {
    this.multiple = this.limit > 1;
  }

  @Watch('maxSize')
  handleMaxBytes() {
    this.maxSizeInBytes = getBytesFromHumanReadableFileSize(this.maxSize);
  }

  /** This event is emitted when you add files. Either via drag and drop or using the file explorer.   */
  @Event() filesAdded: EventEmitter<PnUploadFileItem[]>;
  /** Emitted everytime an update is made to one of the files. */
  @Event() update: EventEmitter<{ files: PnUploadFileItem[]; element: HTMLPnFileUploadElement }>;
  /** If any error occurs, such as: invalid file type, size, limit or error during upload.  */
  @Event() fileuploaderror: EventEmitter<Error>;
  /** Emitted everytime you select a valid file. It's a good idea to use this event in order to make use of the built in validation features. */
  @Event() valid: EventEmitter<this>;
  /** Emitted once the file upload has begun. */
  @Event() uploadFile: EventEmitter<{
    file: PnUploadFileItem;
    index: number;
    uploader: any;
  }>;
  /** Emitted when an upload has been cancelled before completion. */
  @Event() uploadCancelled: EventEmitter<PnUploadFileItem>;
  /** Emitted once the upload is finished. */
  @Event() uploadCompleted: EventEmitter<PnUploadFileItem[]>;

  async componentWillLoad() {
    this.handleLimit();
    this.handleMaxBytes();

    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  /** Start the upload of all selected files (excludes invalid files). */
  @Method()
  async startUpload(): Promise<void> {
    if (!this.files.length) return;

    this.files = this.files.map((file, index) => {
      if (file.status === 'queue') {
        file.status = 'start';
        this.uploadFile.emit({
          file: file,
          index: index,
          uploader: this,
        });
      }

      return file;
    });

    const promises = this.files.map(file => file.xhrPromise().catch(() => {}));

    return Promise.all(promises).then(data => {
      const added = data.filter(item => item);

      this.uploadCompleted.emit(added);
    });
  }

  /** Clear the selected files, state and input value. */
  @Method()
  async clearUpload() {
    this.files = [];
    this.fileInputElement.value = '';
    this.invalid = false;
    this.filesAdded.emit([]);
  }

  /** Remove the selected file. */
  @Method()
  async removeFile(targetFile: PnUploadFileItem) {
    let valid = true;
    const files = [];
    for (const fileIndex in this.files) {
      const file = this.files[fileIndex];

      if (!file.filename) {
        continue;
      }

      // add to files if the uuid doesn't match
      if (file.uuid !== targetFile.uuid) {
        try {
          this._validateFileSize(file);
          this._validateFileType(file);
          valid = true;
        } catch (error) {
          valid = false;
          file.error = error;
          file.progress = 0;
          file.status = 'error';
        }

        files.push(file);
      } else if (file.status === 'start') {
        file.xhr.abort();
        this.uploadCancelled.emit(file);
      }
    }

    this.filesAdded.emit(files);
    this.files = files;
    this.invalid = !valid || !this._isNotGreaterThanLimit();
    this._checkValidity();
    this._emitUpdateEvent();
  }

  /** Update an existing file. */
  @Method()
  async updateFile(file: PnUploadFileItem, index = null) {
    let files = this.files;

    // find the index if not defined
    if (index === null) {
      for (const fIndex in files) {
        const fFile = files[fIndex];

        if (fFile.uuid === file.uuid) {
          index = fIndex;
          break;
        }
      }
    }

    if (files[index]) {
      if (file.progress >= 100) {
        file.progress = 100;
        file.status = 'completed';
      } else if (file.progress < 0) {
        file.status = 'start';
        file.progress = 0;
      }

      if (file.error) {
        file.progress = 0;
        file.status = 'error';
        file.errorMessage = file.errorMessage || 'There was an error uploading your file, please try again';
      }

      files[index] = file;
    }

    this.files = [...files];
  }

  private async _addFilesFromFileList(fileList: FileList): Promise<void> {
    if (this.disabled || this.invalid) {
      return;
    }

    const files = Array.from(fileList);
    const promises: Promise<PnUploadFileItem>[] = [];

    if (this.multiple) {
      files.forEach(file => promises.push(this._addFile(file)));
    } else {
      const file = files[0];

      if (this.limit === 1) {
        this.files = [];

        promises.push(this._addFile(file));
      } else {
        promises.push(this._addFile(file));
      }
    }

    return Promise.all(promises).then(() => {
      this.filesAdded.emit(this.files);

      this._isNotGreaterThanLimit();
      this._checkValidity();
      this._emitUpdateEvent();
    });
  }

  private async _onAddFile() {
    const files = this.fileInputElement.files;
    return await this._addFilesFromFileList(files);
  }

  private _onDragOverFile(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isHovering = true;
  }

  private _onDragLeaveFile(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isHovering = false;
  }

  private _onDropFile(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isHovering = false;

    ripple(event, this.hostElement, '.pn-dropzone-inner');

    this._addFilesFromFileList(event.dataTransfer.files);
  }

  private _validateFileType(file: PnUploadFileItem): void {
    const accepts = this.accept ? this.accept.split(',') : [];

    if (accepts.length) {
      let valid = false;
      for (const accept of accepts) {
        const type = accept.trim().toLowerCase();
        const isMimeType = /^\w+\/[a-z\.\-]+$/i;
        const extension = file.filename.match(/\.\w{3,4}($|\?)/)?.[0]?.toLowerCase();

        if ((isMimeType.test(type) && file.contentType === type) || extension === type) {
          valid = true;
          break;
        }
      }

      if (!valid) {
        const error = new PnFileUploadError<PnUploadFileItem>(
          `Invalid file type.`,
          this.hostElement,
          'INVALID_FILE_TYPE',
          file,
        );

        this.invalid = true;
        this.fileuploaderror.emit(error);

        throw error;
      }
    }
  }

  private _validateFileSize(file: PnUploadFileItem): void {
    if (this.maxSizeInBytes > 0 && file.filesize > this.maxSizeInBytes) {
      const error = new PnFileUploadError<PnUploadFileItem>(
        `You can only add up to ${this.maxSize}.`,
        this.hostElement,
        'MAX_FILE_SIZE',
        file,
      );

      this.invalid = true;
      this.fileuploaderror.emit(error);

      throw error;
    }
  }

  private _validateLimit(): void {
    if (this.limit && this.files.length > this.limit) {
      const error = new PnFileUploadError<PnUploadFileItem[]>(
        `You can only add up to ${this.limit} files.`,
        this.hostElement,
        'MAX_LIMIT',
        this.files,
      );

      this.invalid = true;
      this.fileuploaderror.emit(error);

      throw error;
    }
  }

  private _isNotGreaterThanLimit(): boolean {
    try {
      this._validateLimit();

      return true;
    } catch (error) {
      return false;
    }
  }

  private _checkValidity(): boolean {
    if (!this.invalid) {
      this.valid.emit(this);
    }

    return !this.invalid;
  }

  private _createBase64FromFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
      reader.onabort = () => reject('ABORTED');

      reader.readAsDataURL(file);
    });
  }

  private async _addFile(file: File): Promise<PnUploadFileItem> {
    return this._createBase64FromFile(file).then(data => {
      const split = data.split(',');
      const base64 = split[1];
      const contentType = split[0].split(':')[1].split(';')[0];
      const request = new XMLHttpRequest();

      const uploadFile: PnUploadFileItem = {
        uuid: uuidv4(),
        filename: file.name,
        filesize: file.size,
        progress: 0,
        status: 'queue',
        base64,
        contentType,
        error: null,
        errorMessage: '',
        file,
        xhr: request,
      };

      try {
        this._validateFileSize(uploadFile);
        this._validateFileType(uploadFile);
      } catch (error) {
        uploadFile.error = error;
        uploadFile.progress = 0;
        uploadFile.status = 'error';
      }

      uploadFile.xhrPromise = () => {
        return new Promise((resolve, reject) => {
          request.responseType = 'json';

          request.addEventListener('progress', event => {
            if (event.lengthComputable) {
              uploadFile.progress = (event.loaded / event.total) * 100;

              this.updateFile(uploadFile);
            } else {
              uploadFile.progress = 100;

              this.updateFile(uploadFile);
            }
          });

          request.addEventListener('load', (response: ProgressEvent) =>
            resolve((response.target as XMLHttpRequest).response),
          );

          request.addEventListener('error', error => {
            uploadFile.error = error;
            uploadFile.errorMessage = 'There was an error uploading your file, please try again';

            this.updateFile(uploadFile);

            const errorEvent = new PnFileUploadError<PnUploadFileItem>(
              'There was an error uploading your file, please try again',
              this.hostElement,
              'UPLOAD_SERVER_ERROR',
              uploadFile,
            );

            this.fileuploaderror.emit(errorEvent);

            reject(error);
          });
        });
      };

      this.files = [...this.files, uploadFile];

      return uploadFile;
    });
  }

  private _emitUpdateEvent() {
    this.update.emit({
      files: this.files,
      element: this.hostElement as HTMLPnFileUploadElement,
    });
  }

  translate(prop: string): string {
    return translations?.[prop]?.[this.language || en] || prop;
  }

  isLoading(file: PnUploadFileItem): boolean {
    return file.status === 'start';
  }

  getFileItemClass = (file: PnUploadFileItem) => {
    let itemClass = 'files-list-item';
    if (file.error) {
      itemClass += ' files-list-item-error';
    }
    return itemClass;
  };

  getFileIcon(file: PnUploadFileItem): string {
    if (file.error) return alert_exclamation_circle;
    if (file.progress === 100) return check;
    return attachment;
  }

  getFileColor(file: PnUploadFileItem): PnColors {
    if (file.error) return 'warning';
    if (file.progress === 100) return 'green700';
    return 'gray900';
  }

  describedBy() {
    const list = [this.accept && this.idAccepttext, this.helpertext && this.idHelpertext].filter(Boolean);
    if (!list.length) return null;
    return list.join(',');
  }

  render() {
    return (
      <Host>
        <div class="pn-file-upload-container">
          <div class="pn-dropzone-container">
            <input
              id={this.idFileUpload}
              class="pn-file-upload-input"
              type="file"
              name={this.name}
              multiple={this.multiple}
              accept={this.accept}
              capture={this.capture}
              disabled={this.disabled}
              required={this.required}
              aria-describedby={this.describedBy()}
              onClick={e => ripple(e, this.hostElement, '.pn-dropzone-inner')}
              onChange={() => this._onAddFile()}
              onDragOver={e => this._onDragOverFile(e)}
              onDragLeave={e => this._onDragLeaveFile(e)}
              onDrop={e => this._onDropFile(e)}
              ref={el => (this.fileInputElement = el)}
            />
            <div class="pn-dropzone-inner" data-hover={this.isHovering} data-invalid={this.invalid}>
              <pn-icon class="pn-file-upload-icon-element" icon={upload} color="blue700" />

              <label htmlFor={this.idFileUpload} class="pn-file-upload-title">
                {this.label || this.translate('CLICKORDRAG')}
              </label>

              {this.helpertext && (
                <p id={this.idHelpertext} class="pn-file-upload-subtitle">
                  {this.helpertext}
                </p>
              )}
              {this.accept && (
                <p id={this.idAccepttext} class="pn-file-upload-subtitle">
                  {this.translate('ACCEPTEDFORMATS')}: {this.accept}
                  {this.maxSize && (
                    <span>
                      {' '}
                      ({this.translate('MAXFILESIZE')}: {this.maxSize})
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          {!!this.files.length && (
            <ul class="files-list">
              {this.files.map((file: PnUploadFileItem) => (
                <li class="files-list-item-container" key={file.uuid}>
                  <div class={this.getFileItemClass(file)}>
                    <div class="file-list-item-title">
                      <pn-icon icon={this.getFileIcon(file)} color={this.getFileColor(file)} />
                      {this.hideProgress || file.progress === 0 ? (
                        <div class="filename">{file.filename}</div>
                      ) : (
                        <pn-progress-bar
                          label={file.filename}
                          progress={file.progress}
                          error={file.error && file.errorMessage}
                        />
                      )}
                    </div>

                    <pn-button
                      small
                      variant="borderless"
                      arialabel={`${this.translate('REMOVE_FILE')} ${file.filename}`}
                      appearance={file.error ? 'warning' : 'light'}
                      icon={trash_can}
                      iconOnly={true}
                      tooltip={this.translate('REMOVE_FILE')}
                      loading={this.isLoading(file)}
                      onClick={() => this.removeFile(file)}
                    />
                  </div>
                  {file.error ? (
                    <div class="files-list-item-text-error">{file.errorMessage || file.error.message}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <slot />
        </div>
      </Host>
    );
  }
}
